const QUERY_PARAMS_STORAGE_KEY = "campaign_query_params_v1";
const QUERY_PARAMS_TTL_MS = 14 * 24 * 60 * 60 * 1000;

type StoredQueryParams = {
  base64: string;
  expiresAt: number;
};

type QueryParamsPayload = {
  entries: Array<[string, string]>;
  capturedAt: number;
  path: string;
};

function encodeBase64Utf8(value: string): string {
  const bytes = new TextEncoder().encode(value);
  let binary = "";
  bytes.forEach((byte) => {
    binary += String.fromCharCode(byte);
  });
  return btoa(binary);
}

function readStoredQueryParams(now = Date.now()): StoredQueryParams | null {
  if (typeof window === "undefined") {
    return null;
  }

  try {
    const raw = localStorage.getItem(QUERY_PARAMS_STORAGE_KEY);
    if (!raw) {
      return null;
    }

    const parsed = JSON.parse(raw) as Partial<StoredQueryParams>;
    if (!parsed.base64 || typeof parsed.expiresAt !== "number") {
      localStorage.removeItem(QUERY_PARAMS_STORAGE_KEY);
      return null;
    }

    if (parsed.expiresAt <= now) {
      localStorage.removeItem(QUERY_PARAMS_STORAGE_KEY);
      return null;
    }

    return {
      base64: parsed.base64,
      expiresAt: parsed.expiresAt,
    };
  } catch {
    localStorage.removeItem(QUERY_PARAMS_STORAGE_KEY);
    return null;
  }
}

function writeStoredQueryParams(base64: string, now = Date.now()): void {
  const value: StoredQueryParams = {
    base64,
    expiresAt: now + QUERY_PARAMS_TTL_MS,
  };

  localStorage.setItem(QUERY_PARAMS_STORAGE_KEY, JSON.stringify(value));
}

export function captureAndStoreQueryParamsBase64(
  now = Date.now(),
): string | null {
  if (typeof window === "undefined") {
    return null;
  }

  const params = new URLSearchParams(window.location.search);
  const entries = Array.from(params.entries());

  if (entries.length === 0) {
    return readStoredQueryParams(now)?.base64 ?? null;
  }

  const payload: QueryParamsPayload = {
    entries,
    capturedAt: now,
    path: window.location.pathname,
  };

  const base64 = encodeBase64Utf8(JSON.stringify(payload));
  writeStoredQueryParams(base64, now);
  return base64;
}

export function getStoredQueryParamsBase64(now = Date.now()): string | null {
  return readStoredQueryParams(now)?.base64 ?? null;
}
